import { nanoid } from 'nanoid';

export interface AttributionCtx {
  sessionId: string;
  source?: string;
  channel?: string;
  shortcode?: string;
  postId?: string;
  reviewId?: string;
  restaurantId?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
}

const SESSION_KEY = 'kudoz_session_id';
const ATTRIBUTION_KEY = 'kudoz_attribution';

function initSession(): string {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = nanoid(21);
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

function loadStored(): Partial<AttributionCtx> {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return {};
}

function saveStored(ctx: Partial<AttributionCtx>): void {
  try {
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(ctx));
  } catch {
    // ignore
  }
}

function parseUrlParams(): Partial<AttributionCtx> {
  try {
    const params = new URLSearchParams(window.location.search);
    const result: Partial<AttributionCtx> = {};
    const pick = (key: keyof AttributionCtx, param: string) => {
      const val = params.get(param);
      if (val) (result as Record<string, string>)[key] = val;
    };
    pick('utm_source', 'utm_source');
    pick('utm_medium', 'utm_medium');
    pick('utm_campaign', 'utm_campaign');
    pick('utm_content', 'utm_content');
    pick('shortcode', 'shortcode');
    pick('postId', 'post_id');
    pick('reviewId', 'review_id');
    pick('restaurantId', 'restaurant_id');
    pick('channel', 'platform');
    // Infer source from utm params if not explicitly set
    const utmSrc = params.get('utm_source');
    if (utmSrc === 'kudoz_short') {
      result.source = 'short_link';
    }
    return result;
  } catch {
    return {};
  }
}

// Build the full context for this session
function buildCtx(): AttributionCtx {
  const sessionId = initSession();
  const stored = loadStored();
  const fromUrl = parseUrlParams();
  // Merge: URL params take priority; don't overwrite with nulls
  const merged: Partial<AttributionCtx> = { ...stored };
  for (const [k, v] of Object.entries(fromUrl)) {
    if (v) (merged as Record<string, string>)[k] = v;
  }
  saveStored(merged);
  return { sessionId, ...merged };
}

// Module-level singleton so the provider can be set once
let _ctxProvider: (() => AttributionCtx) | null = null;

export function setAttributionCtxProvider(fn: () => AttributionCtx): void {
  _ctxProvider = fn;
}

export function getAttributionCtxGlobal(): AttributionCtx | null {
  return _ctxProvider ? _ctxProvider() : null;
}

async function postAttributionEvent(
  eventType: string,
  entityType?: string,
  entityId?: string,
  ctx?: AttributionCtx,
  extras?: Record<string, string>,
): Promise<void> {
  try {
    const attribution = ctx || getAttributionCtxGlobal();
    const token = typeof window !== 'undefined' ? window.localStorage.getItem('auth_token') : null;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    await fetch('/api/attribution/event', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        eventType,
        entityType: entityType || null,
        entityId: entityId || null,
        sessionId: attribution?.sessionId,
        shortLinkCode: attribution?.shortcode,
        postId: attribution?.postId,
        reviewId: attribution?.reviewId,
        restaurantId: attribution?.restaurantId,
        utmSource: attribution?.utm_source,
        utmMedium: attribution?.utm_medium,
        utmCampaign: attribution?.utm_campaign,
        source: attribution?.source,
        channel: attribution?.channel,
        ...extras,
      }),
    });
  } catch {
    // fire-and-forget — never throw
  }
}

export function useAttribution(): {
  getAttributionCtx: () => AttributionCtx;
  sendAttributionEvent: (eventType: string, entityType?: string, entityId?: string, extras?: Record<string, string>) => void;
} {
  const ctx = buildCtx();

  const getAttributionCtx = (): AttributionCtx => buildCtx();

  const sendAttributionEvent = (
    eventType: string,
    entityType?: string,
    entityId?: string,
    extras?: Record<string, string>,
  ): void => {
    // fire-and-forget
    postAttributionEvent(eventType, entityType, entityId, ctx, extras);
  };

  return { getAttributionCtx, sendAttributionEvent };
}
